import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/layout/layout";
import { fetchArticleBySlug, fetchPublishedArticles } from "../../lib/articles";
import TableOfContents from "../../components/articles/TableOfContents";
import SocialShare from "../../components/ui/social-share";
import Breadcrumb from "../../components/ui/breadcrumb";
import { supabase } from "../../lib/supabase";
import Button from "../../components/ui/button";
import StaticMetaTags from "../../components/seo/StaticMetaTags";
import ArticleSchema from "../../components/seo/ArticleSchema";

interface Article {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
  author?: string;
  category?: string;
  slug?: string;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [article, setArticle] = useState<Article | null>(null);
  const [latestArticles, setLatestArticles] = useState<Article[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: userData } = await supabase
        .from("users")
        .select("role")
        .eq("id", user.id)
        .single();
      if (userData?.role === "admin") {
        setIsAdmin(true);
      }
    };

    const load = async () => {
      if (!slug) return;
      setIsLoading(true);
      const a = await fetchArticleBySlug(slug);
      const latest = await fetchPublishedArticles();
      setArticle(a);
      setLatestArticles(latest.filter((art) => art.slug !== slug).slice(0, 3));
      setIsLoading(false);
    };

    checkAdmin();
    load();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <>
        <StaticMetaTags
          title="Article introuvable"
          description="L'article que vous recherchez n'existe pas ou a été supprimé."
          url={`/ressources/articles/${slug}`}
        />
        <Layout title="Article introuvable">
          <div className="py-32 text-center text-neutral-500 text-xl">
            😕 Désolé, cet article n'existe pas ou a été supprimé.
          </div>
        </Layout>
      </>
    );
  }

  const getCategoryColor = (category?: string) => {
    const colors: { [key: string]: string } = {
      'Recrutement': 'bg-blue-100 text-blue-800',
      'Marque employeur': 'bg-pink-100 text-pink-800',
      'RH & Management': 'bg-purple-100 text-purple-800',
      'Onboarding': 'bg-green-100 text-green-800',
      'Culture d\'entreprise': 'bg-yellow-100 text-yellow-800',
      'Innovation RH': 'bg-indigo-100 text-indigo-800',
      'Général': 'bg-neutral-100 text-neutral-800'
    };
    return colors[category || 'Général'] || colors['Général'];
  };

  // Générer un extrait optimisé pour le SEO
  const getExcerpt = () => {
    if (article.excerpt) return article.excerpt;
    
    // Extraire le texte du HTML et limiter à 155 caractères (optimal pour Google)
    const textContent = article.content
      .replace(/<[^>]*>/g, '') // Supprimer HTML
      .replace(/\s+/g, ' ')    // Normaliser les espaces
      .trim();
    
    return textContent.length > 155 
      ? textContent.substring(0, 152) + '...'
      : textContent;
  };

  // Générer des mots-clés pour le SEO
  const getKeywords = () => {
    const baseKeywords = ['recrutement', 'RH', 'ressources humaines'];
    if (article.category) baseKeywords.push(article.category);
    if (article.author) baseKeywords.push(article.author);
    return baseKeywords.join(', ');
  };

  // Générer le fil d'Ariane dynamique
  const getBreadcrumbItems = () => {
    const items = [
      { name: "Accueil", href: "/" },
      { name: "Ressources", href: "/ressources" },
      { name: "Articles", href: "/ressources/articles" }
    ];

    // Ajouter la catégorie si elle existe
    if (article.category) {
      items.push({
        name: article.category,
        href: `/ressources/articles?category=${encodeURIComponent(article.category)}`
      });
    }

    // Ajouter l'article actuel
    items.push({
      name: article.title,
      href: `/ressources/articles/${article.slug}`
    });

    return items;
  };

  return (
    <>
      {/* 🚨 CRITIQUES : Meta tags placées EN PREMIER pour le prerendering */}
      <StaticMetaTags
        title={article.title}
        description={getExcerpt()}
        url={`/ressources/articles/${article.slug}`}
        image={article.image_url || 'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png'}
        type="article"
        publishedTime={article.created_at}
        modifiedTime={article.updated_at}
        author={article.author || 'Recruteurs.io'}
        section={article.category || 'RH'}
        tags={article.category ? [article.category, 'recrutement', 'RH'] : ['recrutement', 'RH']}
      />
      <ArticleSchema
        title={article.title}
        description={getExcerpt()}
        url={`https://recruteurs.io/ressources/articles/${article.slug}`}
        image={article.image_url || "https://recruteurs.io/default.jpg"}
        publishedAt={article.created_at}
        updatedAt={article.updated_at}
        author={article.author || "Recruteurs.io"}
        category={article.category || "RH"}
      />

      <Layout 
        title={article.title} 
        description={getExcerpt()}
        keywords={getKeywords()}
        ogImage={article.image_url}
        ogType="article"
      >
        <div className="container-custom py-16">
          {article.image_url && (
            <div className="relative mb-10">
              <img 
                src={article.image_url} 
                alt={article.title} 
                className="w-full rounded-xl shadow-lg max-h-[450px] object-cover" 
                loading="eager"
              />
              {article.category && (
                <div className="absolute top-6 left-6">
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getCategoryColor(article.category)}`}>
                    {article.category}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="mb-10">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-4 text-neutral-900">{article.title}</h1>
                
                {/* Fil d'Ariane sous le titre */}
                <div className="mb-6">
                  <Breadcrumb items={getBreadcrumbItems()} />
                </div>
                
                <div className="flex items-center gap-4 text-sm text-neutral-500 mb-4">
                  {article.author && <span>Par {article.author}</span>}
                  <span>•</span>
                  <span>{new Date(article.created_at).toLocaleDateString("fr-FR", {
                    day: 'numeric',
                    month: 'long', 
                    year: 'numeric'
                  })}</span>
                  {article.category && (
                    <>
                      <span>•</span>
                      <span>{article.category}</span>
                    </>
                  )}
                </div>
                {article.excerpt && (
                  <p className="text-lg text-neutral-600 mb-6 italic">
                    {article.excerpt}
                  </p>
                )}
                
                {/* Composant de partage social */}
                <SocialShare
                  title={article.title}
                  url={`/ressources/articles/${article.slug}`}
                  description={getExcerpt()}
                  className="mb-6"
                />
              </div>
              {isAdmin && (
                <Button variant="outline" onClick={() => navigate(`/ressources/articles/${slug}/edit`)} className="shrink-0">
                  ✏️ Modifier l'article
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-12">
            <article className="lg:w-2/3 prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: article.content }} />

            <aside className="lg:w-1/3">
              <div className="sticky top-20 space-y-8">
                <TableOfContents html={article.content} />

                {/* Partage social dans la sidebar aussi */}
                <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100">
                  <SocialShare
                    title={article.title}
                    url={`/ressources/articles/${article.slug}`}
                    description={getExcerpt()}
                  />
                </div>

                <div className="rounded-xl p-6 bg-gradient-to-br from-pink-100 via-yellow-100 to-blue-100 shadow">
                  <h3 className="text-xl font-bold mb-2">Pimp My Job 🚀</h3>
                  <p className="text-neutral-700 mb-4">Générez des offres d'emploi irrésistibles grâce à l'IA.</p>
                  <a href="https://pimpmyjob.fr" className="inline-block bg-black text-white px-4 py-2 rounded hover:bg-neutral-800 transition">
                    Tester gratuitement
                  </a>
                </div>

                <div className="rounded-xl p-6 bg-gradient-to-r from-blue-100 to-purple-100 shadow">
                  <h3 className="text-xl font-bold mb-2">Mapping RH 🧠</h3>
                  <p className="text-neutral-700 mb-4">Évaluez la compatibilité entre vos candidats et vos équipes.</p>
                  <a href="/ressources/mapping-rh" className="inline-block bg-black text-white px-4 py-2 rounded hover:bg-neutral-800 transition">
                    Tester le mapping
                  </a>
                </div>
              </div>
            </aside>
          </div>

          {latestArticles.length > 0 && (
            <section className="mt-20">
              <h2 className="text-2xl font-bold mb-6">Articles recommandés</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {latestArticles.map((a) => (
                  <article key={a.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 group">
                    {a.image_url && (
                      <div className="aspect-[16/9] overflow-hidden relative">
                        <img 
                          src={a.image_url} 
                          alt={a.title} 
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        {a.category && (
                          <div className="absolute top-4 left-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(a.category)}`}>
                              {a.category}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                        <a href={`/ressources/articles/${a.slug}`}>{a.title}</a>
                      </h3>
                      <p className="text-neutral-600 mb-4 line-clamp-3">{a.excerpt || a.content}</p>
                      <div className="text-sm text-neutral-500">
                        {new Date(a.created_at).toLocaleDateString("fr-FR")}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>
      </Layout>
    </>
  );
}